/// <amd-module name="TFS/Admin/Views/OrganizationOverview/Components/ChangeOwner/ChangeOwner" />
import * as React from "react";

import { IVssPageContext } from "VSS/Platform/Context";
import { Button } from "VSSUI/Button";
import { IOrganizationIdentity } from "TFS/Admin/Common/Contracts/IOrganizationIdentity";
import { MessageBar, MessageBarSeverity } from "VSSUI/MessageBar";
import { VssPersona } from "VSSUI/VssPersona";
import { getIdentityImageUrl } from "VSS/Platform/Identity";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { wait } from "VSS/Core/Util/Promise";
import AdminResources = require("TFS/Admin/Views/OrganizationOverview/Resources");
import { IOrganizationOverviewDelayLoadData, IOwner } from "Contracts/DataModels";
import { Feature } from "TFS/Admin/Common/Constants/TelemetryConstants";
import { AdminTelemetry, AdminFeatureTelemetryLogger } from "TFS/Admin/Common/Util/Telemetry/AdminTelemetry";
import { OrganizationOverviewSource } from "TFS/Admin/Views/OrganizationOverview/Sources/OrganizationOverviewSources";
import { IPanelSelectChange } from "TFS/Admin/Common/Contracts/SelectUserPanel";
import { WrappedComponent } from "VSS/Platform/Layout";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { OrganizationDataProvidersContributions, ConstantNumbers } from "TFS/Admin/Views/OrganizationOverview/Constants/Constants";
import { Link } from "VSSUI/Link";
import { ISelectUserPanelProps } from "TFS/Admin/Common/Controls/SelectUserPanel/SelectUserPanel.Props";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { announce } from "VSS/Core/Util/Accessibility";

export interface IChangeOwnerProps {
    pageContext: IVssPageContext;
    onChangeOwner: (newOwner: IOwner) => void;
    organizationName: string;
    organizationId: string;
    owner: IOwner;
    hasModifyPermissions: boolean;
    hasDeletePermissions: boolean;
    isOwner: boolean;
    organizationTakeover?: {
        isEligibleForTakeOver: boolean;
        isAzureDevOpsAdministrator: boolean;
    };
}

interface IChangeOwnerState {
    showChangeOwnerPanel: boolean;
    updateInProgress: boolean;
    error?: string;
    success?: string;
    showTakeOverMessage: boolean;
    isTakeOver: boolean;
}

export class ChangeOwner extends React.Component<IChangeOwnerProps, IChangeOwnerState> {
    private telemetryLogger: AdminTelemetry = new AdminTelemetry(this.props.pageContext);
    private imageUrl?: string;

    constructor(props: IChangeOwnerProps) {
        super(props);
        this.state = {
            showChangeOwnerPanel: false,
            updateInProgress: false,
            showTakeOverMessage: this.props.organizationTakeover
                ? this.props.organizationTakeover.isEligibleForTakeOver &&
                  isFeatureFlagEnabled(this.props.pageContext, "VisualStudio.Services.AdminEngagement.OrganizationOverview.EnableTakeOverUI", false)
                : false,
            isTakeOver: false
        };
    }

    public render(): JSX.Element {
        this.imageUrl = this._getOwnerImage();

        return (
            <div>
                <div>
                    {this.state.error ? (
                        <MessageBar
                            className="organization-message"
                            severity={MessageBarSeverity.Error}
                            onDismiss={() => this.setState({ error: undefined })}
                        >
                            {this.state.error}
                        </MessageBar>
                    ) : null}
                    {this.state.success ? (
                        <MessageBar
                            className="organization-message"
                            severity={MessageBarSeverity.Success}
                            onDismiss={() => this.setState({ success: undefined })}
                        >
                            {this.state.success}
                        </MessageBar>
                    ) : null}
                    {this.props.organizationTakeover && this.props.organizationTakeover.isEligibleForTakeOver && this.state.showTakeOverMessage ? (
                        <MessageBar
                            className="organization-message"
                            severity={MessageBarSeverity.Warning}
                            onDismiss={() => this.setState({ showTakeOverMessage: false })}
                            buttonProps={[
                                {
                                    text: AdminResources.ChangeOwner,
                                    onClick: this._onTakeOverClick,
                                    disabled: !this.props.organizationTakeover.isAzureDevOpsAdministrator
                                }
                            ]}
                        >
                            <div className="flex-column word-break">
                                {AdminResources.TakeOverWarning}
                                <Link target="_blank" rel="noopener noreferrer" href={AdminResources.TakeOverLearnMoreLink}>
                                    {AdminResources.TakeOverLearnMore}
                                </Link>
                            </div>
                        </MessageBar>
                    ) : null}
                    <div className="title-m">{AdminResources.OrganizationOwner}</div>
                    <div className="flex-row flex-center organization-owner">
                        <VssPersona
                            identityDetailsProvider={{
                                getIdentityImageUrl: (size: number): string | undefined => {
                                    return this.imageUrl;
                                },
                                getDisplayName: (): string => {
                                    return this.props.owner.name;
                                }
                            }}
                        />
                        <div className="organization-owner-details">
                            <div className="fontWeightSemiBold">{this.props.owner.name}</div>
                            <div>{this.props.owner.email}</div>
                        </div>
                    </div>
                    <div className="learn-more-link">
                        <Link target="_blank" rel="noopener noreferrer" href={AdminResources.ChangeOwnerLearnMoreLink}>
                            {AdminResources.ChangeOwnerLearnMore}
                        </Link>
                    </div>
                    <div className="change-owner">
                        <Button disabled={this.state.updateInProgress || !this.props.hasModifyPermissions} onClick={this._onChangeOwnerClick}>
                            {AdminResources.ChangeOwner}
                        </Button>
                    </div>
                    <div className="overview-divider" />
                </div>

                {this.state.showChangeOwnerPanel ? (
                    <WrappedComponent<ISelectUserPanelProps>
                        dependencies={["ms.vss-admin-web.admin-common"]}
                        wrappedType={"ms.vss-admin-web.select-user-panel"}
                        wrappedProps={{
                            pageContext: this.props.pageContext,
                            onCancel: this._onCancelChangeOwner,
                            onSelectUser: this._onChangeOwner,
                            panelTitle: AdminResources.ChangeOrganizationOwnerTitle,
                            actionButtonText: AdminResources.ChangeButton,
                            identityTypesToLookup: ["user", "group"],
                            onUserChange: this._onOwnerSelected,
                            selectUserPlaceHolderText: AdminResources.SearchUsers,
                            selectUserEditPlaceHolderText: AdminResources.SearchUsers,
                            requestReason: this.state.isTakeOver
                        }}
                    />
                ) : null}
            </div>
        );
    }

    private _startActionCapture = (action: string, properties: { [key: string]: any }): AdminFeatureTelemetryLogger => {
        return this.telemetryLogger.startActionCapture(Feature.OrganizationOverview, Feature.OrganizationOverview, action, properties);
    };

    protected _onOwnerSelected = (selectedUser?: IOrganizationIdentity): IPanelSelectChange => {
        var error: IPanelSelectChange = {
            hasError: true,
            enableActionButton: false
        };
        if (!selectedUser) {
            error.errorComponent = <div>{AdminResources.SelectedUserNotFoundError}</div>;
            return error;
        } else if (!selectedUser.localId) {
            error.errorComponent = <FormatComponent format={AdminResources.SelectedUserErrorText}>{this.props.organizationName}</FormatComponent>;
            return error;
        } else if (selectedUser.entityType != "User") {
            error.errorComponent = <div>{AdminResources.SelectedGroupError}</div>;
            return error;
        } else if (selectedUser.localId === this.props.owner.id) {
            error.errorComponent = <div>{AdminResources.SelectedSameUserError}</div>;
            return error;
        }
        return { hasError: false, enableActionButton: true };
    };

    private _onChangeOwner = async (newOwner?: IOrganizationIdentity, reason?: string) => {
        this.setState({ updateInProgress: true });
        var source = new OrganizationOverviewSource();

        if (newOwner && newOwner.localId) {
            var updateOwnerPromise;
            if (this.state.isTakeOver) {
                var actionTelemetryLogger = this._startActionCapture("Take Over Organization", {});

                updateOwnerPromise = source.forceUpdateCollectionOwner(
                    reason ? reason : "Not included",
                    this.props.organizationId,
                    this.props.pageContext,
                    newOwner.localId
                );
            } else {
                var actionTelemetryLogger = this._startActionCapture("Update Organization Owner", {
                    UpdatedByCurrentOwner: this.props.isOwner ? "true" : "false"
                });
                updateOwnerPromise = source.updateCollectionOwner(newOwner.localId, this.props.pageContext);
            }
            updateOwnerPromise.then(
                async collection => {
                    if (collection.name === this.props.organizationName) {
                        await this._refreshPageDataForOwnerChange();
                    }
                },
                reason => {
                    announce(reason.message);
                    this.setState({ updateInProgress: false, error: reason.message || String(reason), showChangeOwnerPanel: false });
                    actionTelemetryLogger.captureError(reason.message);
                }
            );
        }
    };

    private _onCancelChangeOwner = () => {
        this.setState({ showChangeOwnerPanel: false, isTakeOver: false });
    };

    private _onChangeOwnerClick = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
        this._startActionCapture("Show change owner panel", {});
        this.setState({ showChangeOwnerPanel: true });
    };

    private _onTakeOverClick = async (e: React.MouseEvent<HTMLButtonElement>): Promise<void> => {
        this._startActionCapture("Show change owner panel for takeover", {});
        this.setState({ showChangeOwnerPanel: true, isTakeOver: true });
    };

    private async _refreshPageDataForOwnerChange(): Promise<void> {
        const contributionService = this.props.pageContext.getService<IVssContributionService>("IVssContributionService");
        var actionTelemetryLogger = this._startActionCapture("Fetching new data after change owner", {});
        try {
            let i = 0;
            while (this.state.updateInProgress === true && i++ < 100) {
                var newdata = await contributionService.getDataAsync<IOrganizationOverviewDelayLoadData>(
                    OrganizationDataProvidersContributions.DelayLoadDataProviderId,
                    undefined /*RequestParameters*/,
                    true /*ignoreCache*/
                );
                if (newdata && this.props.owner.id !== newdata.currentOwner.id) {
                    this.imageUrl = undefined;
                    announce(AdminResources.UpdateOwnerSuccess);
                    this.setState({
                        updateInProgress: false,
                        showChangeOwnerPanel: false,
                        success: AdminResources.UpdateOwnerSuccess,
                        showTakeOverMessage: false
                    });
                    // if the previous owner did change owner, we need to update their modify permissions so the state of the edit section looks correct.
                    this.props.onChangeOwner(newdata.currentOwner);
                }
                await wait(ConstantNumbers.ProgressPollInterval);
            }
        } catch (ex) {
            actionTelemetryLogger.captureError(ex.message);
            announce(AdminResources.NewOwnerFetchError);
            this.setState({ updateInProgress: false, showChangeOwnerPanel: false, error: AdminResources.NewOwnerFetchError });
        }
    }

    private _getOwnerImage() {
        var url = getIdentityImageUrl(this.props.pageContext, this.props.owner.id);
        return url;
    }
}
